import { msg } from "@lingui/core/macro";

/**
 * How each dashboard and analytics figure is calculated, shown in the info
 * tooltip next to it (FormulaTip / StatCard `info`). One place, so the
 * definitions can be read and checked together.
 *
 * Every text describes what the backend computes today. When a calculation
 * changes, change its text here too. Sources: the handler / service / SQL
 * behind each figure (cockpit, capacity, reconciliation, workflows).
 */
export const FORMULA = {
  // --- Dashboard (Overview) ---
  dashboardChildrenToday: msg({
    id: "formula.dashboard.childrenToday",
    message:
      "Today: children checked in now / children booked today (full-day absences removed).\nOther days: children in each group's busiest service slot / places.\nWeighted: weighted children / weighted places; a group without weighted places counts its plain places.",
  }),
  dashboardStaffCoverage: msg({
    id: "formula.dashboard.staffCoverage",
    message:
      "Staff with a planned shift on this day (drafts included) / staff the staffing model requires for the booked children.\nGroups in the location and group filter, added up.",
  }),
  dashboardOccupancy: msg({
    id: "formula.dashboard.occupancy",
    message:
      "Weighted children / weighted places x 100, rounded. Only groups with weighted places.\nChildren: busiest service slot of the day. A baby weighs more (capacity settings, default 1.5).\nSparkline: weekly average of the last 30 days, weekends included.",
  }),
  dashboardApprovals: msg({
    id: "formula.dashboard.approvals",
    message:
      "Open workflow items not yet completed. Dunning items count only when blocked.\nOverdue: open items whose due time has passed.",
  }),
  dashboardGroupOverview: msg({
    id: "formula.dashboard.groupOverview",
    message:
      "Per group, in the busiest service slot of the day or in the service you pick: children / places and weighted children / weighted places.\nBar: weighted occupancy, coloured by your capacity thresholds. Arrived: checked in now / booked today.",
  }),
  dashboardUnderstaffedToday: msg({
    id: "formula.dashboard.understaffedToday",
    message: "Groups whose planned staff on this day is below the staffing requirement (warning or critical). All groups.",
  }),
  dashboardUnderstaffedAhead: msg({
    id: "formula.dashboard.understaffedAhead",
    message: "Groups short of planned staff on at least one of the next 7 days (tomorrow to +7). Each group counts once.",
  }),
  dashboardOverdueTasks: msg({
    id: "formula.dashboard.overdueTasks",
    message: "Open tasks whose due time has passed.",
  }),
  dashboardDueToday: msg({
    id: "formula.dashboard.dueToday",
    message: "Open tasks due today. Can include tasks that are already overdue.",
  }),
  dashboardUnreadMessages: msg({
    id: "formula.dashboard.unreadMessages",
    message: "Messages you have not read, in all conversations (families and staff). Your own messages do not count.",
  }),
  dashboardPendingAbsences: msg({
    id: "formula.dashboard.pendingAbsences",
    message: "Staff absence requests that wait for your decision as an approver.",
  }),
  dashboardPendingTimeEntries: msg({
    id: "formula.dashboard.pendingTimeEntries",
    message: "Time entries submitted by staff that wait for approval.",
  }),
  dashboardOverdueInvoices: msg({
    id: "formula.dashboard.overdueInvoices",
    message:
      "Invoices past their due date that still have an unpaid part (parent or subsidy share).\nAmount: the sum still open on these invoices.",
  }),
  dashboardSubsidyExpiring: msg({
    id: "formula.dashboard.subsidyExpiring",
    message: "Beitragsfaktor and SBU dates that end within 60 days, or have already ended and are not renewed.",
  }),
  dashboardCapacityItems: msg({
    id: "formula.dashboard.capacityItems",
    message:
      "Next 60 days from today: days at or above your capacity warning threshold, children who reach the group's age limit, and allocations that are out of sync with the contract or overlap.",
  }),
  dashboardLiveStaffing: msg({
    id: "formula.dashboard.liveStaffing",
    message:
      "Staff: a published or confirmed shift that covers this minute. Children: checked in now.\nRequired staff: calculated from the children checked in now.\nGroups are added up: someone in two groups counts twice.",
  }),
  dashboardRosterChanges: msg({
    id: "formula.dashboard.rosterChanges",
    message:
      "Next 30 days, only groups you have shifts in.\nArriving: allocations that start after today. Departing: allocations that end from today on. Age-type changes do not count.",
  }),
  dashboardMeals: msg({
    id: "formula.dashboard.meals",
    message: "Children booked in each group's busiest service slot on this day, added up. Not taken from the kitchen plan.",
  }),
  dashboardActivities: msg({
    id: "formula.dashboard.activities",
    message: "All activity entries of any type on this day (local day, midnight to midnight), all groups.",
  }),
  dashboardSettlingIn: msg({
    id: "formula.dashboard.settlingIn",
    message: "Settling-in plans running today (started, not complete), overdue plans included. Plans that have not started yet do not count.",
  }),
  dashboardSubsidyDaysRemaining: msg({
    id: "formula.dashboard.subsidyDaysRemaining",
    message:
      "End date minus today, in days. Negative: already ended.\nShows dates within 90 days. A row stays until a newer contract version has a later date.",
  }),

  // --- Analytics: Finance ---
  financeRevenue: msg({
    id: "formula.finance.revenue",
    message:
      "Invoice amounts (parent + subsidy share) whose service month is in the period.\nCounted: finalised, sent, paid, partly paid, overdue. Not counted: drafts, cancelled, credited.\nChange: vs the same months last year.",
  }),
  financeOutstanding: msg({
    id: "formula.finance.outstanding",
    message:
      "Open amount today, all debtors (parents + subsidy offices): invoice total - paid. Drafts included; credit not deducted.\nOverdue: invoices past their due date with an unpaid part.\nPeriod and location filters do not apply.",
  }),
  financeUnappliedCredit: msg({
    id: "formula.finance.unappliedCredit",
    message: "Money received but not yet applied to an invoice, added up over all debtor accounts (parents + subsidy offices), as of today.",
  }),
  financeSubsidyReceivable: msg({
    id: "formula.finance.subsidyReceivable",
    message:
      "Subsidy share still owed by subsidy offices today: subsidy amount - paid, over all open invoices (drafts included). Office credit not deducted.",
  }),
  financeAutoMatchRate: msg({
    id: "formula.finance.autoMatchRate",
    message:
      "Bank payments matched automatically (QR reference, IBAN + amount, rule, or subsidy-office match) / all imported bank payments, by count.\nImports of the last 30 days.",
  }),
  financeBilledVsCollected: msg({
    id: "formula.finance.billedVsCollected",
    message:
      "Billed: invoice amounts by the month they were finalised.\nCollected: payments by value date, including money not yet applied.\nOutstanding: open amount at each month end. All locations.",
  }),
  financeAging: msg({
    id: "formula.finance.aging",
    message:
      "Today's open amount by days past the invoice due date: not yet due, 1-30, 31-60, 61-90, over 90. The parts add up to Outstanding.",
  }),
  financeDunning: msg({
    id: "formula.finance.dunning",
    message:
      "Debtors at each reminder stage now; open cases = sum of the stages.\nCounts debtors, not invoices or amounts. All locations.",
  }),
  financeSubsidyOffices: msg({
    id: "formula.finance.subsidyOffices",
    message:
      "Invoices whose service period lies in the selected months.\nExpected: subsidy share billed (drafts included). Paid: applied so far. Outstanding: expected - paid.",
  }),
  financeInvoiceStatus: msg({
    id: "formula.finance.invoiceStatus",
    message:
      "Invoices of the service month {month} (last month of the period) with this status.\nOverdue: status set to Overdue, not past the due date. Cancelling removes an invoice, so Cancelled stays near 0.",
  }),
  financeCollectionRate: msg({
    id: "formula.finance.collectionRate",
    message:
      "Payments received (by value date) / amounts billed (by month finalised) in the period, parents + subsidy offices.\nCan be above 100 %. Change in percentage points vs last year.",
  }),
  financeAttentionReview: msg({
    id: "formula.finance.attentionReview",
    message: "Parent payments not fully applied and not parked as credit. They wait for manual matching.",
  }),
  financeAttentionMissing: msg({
    id: "formula.finance.attentionMissing",
    message:
      "Child-months since billing began (this month included) with an active contract but no finalised invoice. Drafts do not count. Counted per payer account.",
  }),
  financeAttentionOverdue: msg({
    id: "formula.finance.attentionOverdue",
    message: "Invoices past their due date that still have an unpaid part (parent or subsidy share). Counted per invoice.",
  }),
  financeCashIn: msg({
    id: "formula.finance.cashIn",
    message:
      "Payments received by value date in the period, including money not yet applied.\nWith a location or group filter, only the part applied to that scope counts.",
  }),

  // --- Analytics: Forecast ---
  forecastProjectedYear: msg({
    id: "formula.forecast.projectedYear",
    message:
      "Invoiced + not yet invoiced + contracted revenue for the months of {year}, by service month.\nContracted: billing dry run of today's contracts for the remaining months; no new enrolments. Estimated extras not included.",
  }),
  forecastVsPrior: msg({
    id: "formula.forecast.vsPrior",
    message: "(Projected {year} - projected {prior}) / projected {prior} x 100. Both: invoiced + not yet invoiced + contracted.",
  }),
  forecastYtdReceived: msg({
    id: "formula.forecast.ytdReceived",
    message:
      "Payments on invoices for January to this month, by service month (not payment date).\nChange: vs the same months last year, as paid by today.",
  }),
  forecastOccupancy: msg({
    id: "formula.forecast.occupancy",
    message:
      "Children with a group allocation today / sum of the groups' maximum headcount.\nNot weighted, no absences: differs from the weighted occupancy on the Children tab.",
  }),
  forecastRevpas: msg({
    id: "formula.forecast.revpas",
    message: "Projected {year} revenue / licensed places (sum of the groups' maximum headcount). Revenue per place for the whole year.",
  }),
  forecastThisMonthInvoiced: msg({
    id: "formula.forecast.thisMonthInvoiced",
    message: "Finalised invoices for the current service month, cancelled excluded. P: parents, S: subsidy offices.",
  }),
  forecastArParents: msg({
    id: "formula.forecast.arParents",
    message: "Open balance (amount - paid) of all finalised parent invoice parts, any service month, due or not yet due.",
  }),
  forecastArSubsidy: msg({
    id: "formula.forecast.arSubsidy",
    message: "Open balance (amount - paid) of all finalised subsidy-office invoice parts, any service month, due or not yet due.",
  }),
  forecastUninvoiced: msg({
    id: "formula.forecast.uninvoiced",
    message: "Billable months from 12 months ago to this month without a finalised invoice, valued by a billing dry run of the contracts.",
  }),
  forecastPacing: msg({
    id: "formula.forecast.pacing",
    message:
      "Projected {year} revenue - annual revenue target (billing settings). Shown only without a location, group or payer filter.\nChart: cumulative payments by service month from January, this year vs last year.",
  }),
  forecastPacingNextYear: msg({
    id: "formula.forecast.pacingNextYear",
    message:
      "Projected {year} revenue - annual revenue target (billing settings). Shown only without a location, group or payer filter.\nChart: running total of invoiced + not yet invoiced + contracted by service month, {year} vs {priorYear}.",
  }),
  forecastHeroChart: msg({
    id: "formula.forecast.heroChart",
    message:
      "Bars by service month, or by year in the yearly view. Line: running total of invoiced + not yet invoiced + contracted from the first month shown.\nBand: +/- 3 % per month ahead (max. 30 %), extra -15 % in July and August.",
  }),
  forecastHeroChartDaily: msg({
    id: "formula.forecast.heroChartDaily",
    message: "Bars by day: invoiced by finalisation date, received by receipt date. No projection.",
  }),
  forecastBreakdown: msg({
    id: "formula.forecast.breakdown",
    message: "Total: invoiced + not yet invoiced + contracted for the row. Received and estimated extras are shown but not added.",
  }),
  forecastRevenueMix: msg({
    id: "formula.forecast.revenueMix",
    message: "Projected {year} revenue by payer: parent share vs subsidy-office share of invoiced + not yet invoiced + contracted.",
  }),

  // --- Analytics: Children & contracts ---
  childrenActiveChildren: msg({
    id: "formula.children.activeChildren",
    message: "Distinct children with a contract (not cancelled) whose start and end dates include {date}. All locations; the filter does not apply.",
  }),
  childrenActiveContracts: msg({
    id: "formula.children.activeContracts",
    message: "Contracts (not cancelled) whose start and end dates include {date}. A child with two contracts counts twice. All locations.",
  }),
  childrenNewContracts: msg({
    id: "formula.children.newContracts",
    message: "Children whose enrolment starts in the period. Contract changes and returns within 3 months count as one enrolment. All locations.",
  }),
  childrenContractsEnding: msg({
    id: "formula.children.contractsEnding",
    message: "Children whose enrolment ends in the period, scheduled ends included. A return within 3 months is not an ending. All locations.",
  }),
  childrenSettlingIn: msg({
    id: "formula.children.settlingIn",
    message: "Open settling-in plans that started by today, overdue plans included. The period does not apply. All locations.",
  }),
  childrenOccupancy: msg({
    id: "formula.children.occupancy",
    message:
      "Weighted children booked on {date} / weighted places. Babies weigh more; absent children excluded; busiest service slot if service times are set.",
  }),
  childrenContractMovements: msg({
    id: "formula.children.contractMovements",
    message:
      "Contract versions that start / end per month. An amendment counts as one new and one ended, so this differs from the per-child figures above.",
  }),
  childrenGroupWeek: msg({
    id: "formula.children.groupWeek",
    message: "Weighted children booked per weekday (absences excluded) against the group's weighted places (dashed line).",
  }),
  childrenDocumentation: msg({
    id: "formula.children.documentation",
    message:
      "Documented portfolio entries per child per month (drafts and talks excluded). Children: active children in a group that month.",
  }),

  // --- Analytics: Staff ---
  staffActiveEmployees: msg({
    id: "formula.staff.activeEmployees",
    message:
      "Employees whose status is Active today, all locations. The period does not change this number.",
  }),
  staffCriticalCoverage: msg({
    id: "formula.staff.criticalCoverage",
    message:
      "Groups whose scheduled staff / required staff is below the critical threshold on the as-of day.\nDraft shifts count; children at the busiest service slot.",
  }),
  staffOpenTimesheets: msg({
    id: "formula.staff.openTimesheets",
    message:
      "Time entries still in status Open (not submitted), any date, all locations.",
  }),
  staffAbsenceDays: msg({
    id: "formula.staff.absenceDays",
    message:
      "Approved absence days of all kinds, vacation included: Mon-Fri days in the period, half days 0.5, public holidays not deducted.\nScope: the employee's current groups.",
  }),
  staffCompliance: msg({
    id: "formula.staff.compliance",
    message:
      "100 - share of group-days (Mon-Fri, groups with children) where scheduled staff was below required staff.",
  }),
  staffAppAdoption: msg({
    id: "formula.staff.appAdoption",
    message:
      "People who recorded at least one child activity in the last month of the period / all active employees. Messages do not count.",
  }),
  staffNextDays: msg({
    id: "formula.staff.nextDays",
    message:
      "Groups from tomorrow to 7 days ahead whose planned staff is below the warning or critical threshold, or short of qualified staff.",
  }),
  staffSickRate: msg({
    id: "formula.staff.sickRate",
    message:
      "Sick hours / target hours x 100, approved absences up to today.\nTarget: contract hours per day on Mon-Fri, public holidays excluded. Child sick counts only with the switch on.",
  }),
  staffAccidentRate: msg({
    id: "formula.staff.accidentRate",
    message:
      "Accident hours / target hours x 100. BU: work accidents, NBU: non-work accidents. Unclassified accidents count only in the total.",
  }),
  staffAbsenceRate: msg({
    id: "formula.staff.absenceRate",
    message:
      "(Sick + accident + other absence hours) / target hours x 100. Vacation and comp time are not included.",
  }),
  staffSickDaysPerFte: msg({
    id: "formula.staff.sickDaysPerFte",
    message:
      "Sick days in the period / average FTE of its months (employment % on each person's first workday of the month). Not annualised.",
  }),
  staffSickCases: msg({
    id: "formula.staff.sickCases",
    message:
      "Sick absence records with at least one counted workday in the period. A case that spans months counts in each month.",
  }),
  staffAvgCaseDays: msg({
    id: "formula.staff.avgCaseDays",
    message:
      "Sick days in the period / sickness cases. Only the days inside the period count.",
  }),
  staffAbsenceChart: msg({
    id: "formula.staff.absenceChart",
    message:
      "Absence hours per month against contract hours: sick, accident, other (vacation and comp time excluded). Approved absences, Mon-Fri, public holidays excluded.",
  }),
  staffRanking: msg({
    id: "formula.staff.ranking",
    message:
      "Sickness rate per row over the period. A person in several groups counts fully in each. Marked rows are above the overall rate.",
  }),
  staffAbsenceYear: msg({
    id: "formula.staff.absenceYear",
    message:
      "This person's figures for the calendar year up to today, against their own contract hours. Location and group filters do not apply.",
  }),
  staffWeeklyStaffing: msg({
    id: "formula.staff.weeklyStaffing",
    message:
      "Scheduled / needed staff per group and day. Every shift that is not cancelled counts (drafts included). Needed: staffing model for the children in the busiest service slot.",
  }),

  // --- Analytics: Operations ---
  opsActivities: msg({
    id: "formula.ops.activities",
    message:
      "All recorded child activities of every type (check-in and check-out included) from the first to the last day of the period, Zurich time. All locations.",
  }),
  opsCourseEnrollments: msg({
    id: "formula.ops.courseEnrollments",
    message:
      "Enrolments, counted once for each month they run.\nFill rate: enrolments / course places per month, averaged over all months; months without courses count 0 %.",
  }),
  opsOpenApprovals: msg({
    id: "formula.ops.openApprovals",
    message:
      "Open workflow items not yet completed, plus dunning items blocked on an operator action. All dates, all locations.",
  }),
  opsEvents: msg({
    id: "formula.ops.events",
    message:
      "Calendar events between the first and last day of the period that match the location and group filter.",
  }),

  // --- Analytics: Master data ---
  masterOccupancy: msg({
    id: "formula.masterData.occupancy",
    message:
      "Today: weighted children / weighted places x 100, groups with weighted places only. Children at the busiest service slot; absent children excluded.",
  }),
  masterUnweightedOccupancy: msg({
    id: "formula.masterData.unweightedOccupancy",
    message:
      "Today: children at the busiest service slot / licensed places. Groups without a place limit still add their children.",
  }),
  masterAvgBookedDays: msg({
    id: "formula.masterData.avgBookedDays",
    message:
      "Booked weekdays (Mon-Fri) of active children's current group allocations / active children with an allocation today. From allocations, not contracts.",
  }),
  masterWeekdayHeadcount: msg({
    id: "formula.masterData.weekdayHeadcount",
    message:
      "Active children whose current allocation books this weekday. A child in two groups counts once.",
  }),
  masterFte: msg({
    id: "formula.masterData.fte",
    message:
      "Sum of employment % / 100 of current work contracts, for staff with a contract on a workday this month so far.",
  }),
  masterAvgWorkload: msg({
    id: "formula.masterData.avgWorkload",
    message:
      "FTE x 100 / staff with a current work contract: the average employment %.",
  }),
  masterGroupsCapacity: msg({
    id: "formula.masterData.groupsCapacity",
    message:
      "Weighted children today at the busiest service slot. Free: weighted places - weighted children (negative: over capacity).",
  }),
} as const;
